import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllUsersAsync } from '@/data/store/actions';
import { getAllUsers } from '@/data/store/selectors';
import { RequestParams, TUserItem } from '@/data/types';
import { PersonnelHeader, PersonnelStats, PersonnelControls, PersonnelList } from './components';

const Personnel: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');

  // Redux selectors
  const usersData = useSelector(getAllUsers);

  // API action wrapper
  const attemptGetAllUsers = useCallback(
    async (params: RequestParams) => {
      const searchParams = searchTerm.trim() ? { ...params, search: searchTerm.trim() } : params;
      return await dispatch(getAllUsersAsync(searchParams));
    },
    [dispatch, searchTerm],
  );

  // Helper functions for user data mapping - memoized to prevent re-renders
  const getUserInitials = useCallback((user: TUserItem): string => {
    return `${user.first_name?.charAt(0) || '?'}${user.last_name?.charAt(0) || '?'}`.toUpperCase();
  }, []);

  const getUserRole = useCallback((user: TUserItem): string => {
    // Get the primary role name, fallback to first role or "Χρήστης"
    const primaryRole = user.roles?.[0]?.name || 'Χρήστης';
    return primaryRole;
  }, []);

  const getUserDepartment = useCallback(
    (user: TUserItem): string => {
      // Extract department from role or profile, fallback to "Γενικό"
      const role = getUserRole(user);

      // Map common role patterns to departments
      if (role.includes('Engineer') || role.includes('Μηχανικός')) return 'Μηχανική';
      if (role.includes('Manager') || role.includes('Διευθυντής')) return 'Διοίκηση';
      if (role.includes('Technician') || role.includes('Τεχνικός')) return 'Τεχνική Υποστήριξη';
      if (role.includes('Analyst') || role.includes('Αναλυτής')) return 'Ανάλυση Δεδομένων';
      if (role.includes('Admin') || role.includes('Διαχειριστής')) return 'Διαχείριση';

      return 'Γενικό';
    },
    [getUserRole],
  );

  const getPermissionsCount = useCallback((user: TUserItem): number => {
    return user.permissions?.length || 0;
  }, []);

  const getUserStatus = useCallback((user: TUserItem): 'active' | 'inactive' => {
    // You might want to add a status field to your API
    // For now, assume all users are active
    return 'active';
  }, []);

  const formatJoinDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('el-GR');
  }, []);

  // Mock rating for now - you might want to add this to your API
  const getUserRating = useCallback((user: TUserItem): number => {
    // Generate consistent rating based on user ID
    const hash = user.id.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return Math.abs(hash % 11) / 10 + 4.0; // Range 4.0-5.0
  }, []);

  // Mock projects count - you might want to add this to your API
  const getUserProjectsCount = useCallback((user: TUserItem): number => {
    const hash = user.id.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return Math.abs(hash % 25) + 1; // Range 1-25
  }, []);

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  }, []);

  const getDepartmentColor = useCallback((department: string) => {
    const colors = [
      'bg-blue-50 text-blue-700',
      'bg-purple-50 text-purple-700',
      'bg-green-50 text-green-700',
      'bg-orange-50 text-orange-700',
      'bg-pink-50 text-pink-700',
      'bg-indigo-50 text-indigo-700',
    ];
    return colors[department.length % colors.length];
  }, []);

  // Calculate stats from real data
  const stats = useMemo(() => {
    const total = usersData?.length || 0;
    const active = usersData?.filter((user) => getUserStatus(user) === 'active').length || 0;
    const departments = new Set(usersData?.map((user) => getUserDepartment(user))).size || 0;
    const thisMonth =
      usersData?.filter((user) => {
        const userDate = new Date(user.created_at);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return userDate >= oneMonthAgo;
      }).length || 0;

    return { total, active, departments, thisMonth };
  }, [usersData, getUserStatus, getUserDepartment]);

  // Event handlers
  const handleInviteClick = useCallback(() => {
    navigate('/personnel/invite');
  }, [navigate]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleViewModeChange = useCallback((mode: 'grid' | 'list') => {
    setViewMode(mode);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 p-6">
        <div className="mx-auto max-w-7xl">
          <PersonnelHeader onInviteClick={handleInviteClick} />

          <PersonnelStats stats={stats} />

          <PersonnelControls
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />

          <PersonnelList
            attemptGetAllUsers={attemptGetAllUsers}
            usersData={usersData}
            viewMode={viewMode}
            searchTerm={searchTerm}
            getUserInitials={getUserInitials}
            getUserRole={getUserRole}
            getUserDepartment={getUserDepartment}
            getUserStatus={getUserStatus}
            getUserRating={getUserRating}
            getUserProjectsCount={getUserProjectsCount}
            getPermissionsCount={getPermissionsCount}
            formatJoinDate={formatJoinDate}
            getStatusColor={getStatusColor}
            getDepartmentColor={getDepartmentColor}
          />
        </div>
      </div>
    </>
  );
};

export default Personnel;
